/**
 * Atelier hero — scroll parallax fallback.
 *
 * Publishes a single custom property, `--atelier-hero-progress`, that runs
 * from -1 when the section enters the viewport to +1 when it leaves. The
 * section stylesheet multiplies that number by the per-image rate (-8% for the
 * primary frame, +5% for the secondary one), so all of the motion design lives
 * in CSS and this file only measures.
 *
 * The element stands down entirely when the browser can resolve the same
 * movement from a `view()` timeline, and when the visitor asks for reduced
 * motion. Everything it attaches is removed in `disconnectedCallback`, which
 * is also what makes it safe under the theme editor's section re-renders.
 */

const REDUCED_MOTION_QUERY = '(prefers-reduced-motion: reduce)';
const PROGRESS_PROPERTY = '--atelier-hero-progress';
const OBSERVER_OPTIONS = { rootMargin: '25% 0px 25% 0px' };

class AtelierHero extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {number} */
  #frame = 0;

  /** @type {boolean} */
  #listening = false;

  connectedCallback() {
    if (this.#observer || !this.#shouldAnimate()) return;

    this.#observer = new IntersectionObserver(this.#handleIntersection, OBSERVER_OPTIONS);
    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#stopListening();
    this.#cancelFrame();
    this.style.removeProperty(PROGRESS_PROPERTY);
  }

  /**
   * @returns {boolean} True when this element has to drive the parallax itself.
   */
  #shouldAnimate() {
    if (typeof window.matchMedia === 'function' && window.matchMedia(REDUCED_MOTION_QUERY).matches) {
      return false;
    }

    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function') {
      if (CSS.supports('animation-timeline', 'view()')) return false;
    }

    return true;
  }

  /**
   * @param {IntersectionObserverEntry[]} entries
   */
  #handleIntersection = (entries) => {
    const visible = entries.some((entry) => entry.isIntersecting);

    if (visible === this.#listening) return;

    if (visible) {
      this.#startListening();
      this.#requestFrame();
    } else {
      this.#stopListening();
      this.#cancelFrame();
    }
  };

  #startListening() {
    if (this.#listening) return;

    window.addEventListener('scroll', this.#requestFrame, { passive: true });
    window.addEventListener('resize', this.#requestFrame, { passive: true });
    this.#listening = true;
  }

  #stopListening() {
    if (!this.#listening) return;

    window.removeEventListener('scroll', this.#requestFrame);
    window.removeEventListener('resize', this.#requestFrame);
    this.#listening = false;
  }

  #requestFrame = () => {
    if (this.#frame) return;

    this.#frame = window.requestAnimationFrame(this.#update);
  };

  #cancelFrame() {
    if (!this.#frame) return;

    window.cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #update = () => {
    this.#frame = 0;

    const viewport = window.innerHeight || document.documentElement.clientHeight;
    const rect = this.getBoundingClientRect();
    const travel = viewport + rect.height;

    if (travel <= 0) return;

    const entered = (viewport - rect.top) / travel;
    const clamped = Math.min(Math.max(entered, 0), 1);

    this.style.setProperty(PROGRESS_PROPERTY, (clamped * 2 - 1).toFixed(4));
  };
}

if (!customElements.get('atelier-hero')) {
  customElements.define('atelier-hero', AtelierHero);
}
